import { Button, Divider, Input, Select } from 'antd';
import { useContext, useEffect, useState } from 'react';
import LabelText from '../../General/AntComponents/Typography/Labels/LabelText';
import InfoCallOut from '../../General/AntComponents/Typography/Responsive/InfoCallOut';
import ImageContainer from '../../General/ImageContainer';
import { checkIfObjectIsEmpty } from '../../Helpers/Calculations';
import { FormContext } from '../FormEngine';
import { venclextaSpecialRule } from '../SpecialRules/GenentechHub';
import './ButtonStyle.css';
import { drugList, drugListDisplayNames, genentechHubFieldTypes, genentechHubPharmacyDictionary } from './GenentechHubPharmacyDictionary';
import './SelectInputStyle.css';
import './TextInputStyle.css';

const styles = {
    row: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: '10px'
    },
    column: {
        display: 'flex',
        flexDirection: 'column'
    },
    plusIcon: {
        width: '14px',
        fontColor: 'grey',
        marginTop: '-3px',
        marginLeft: '-1px'
    },
    closeIcon: {
        width: '12px',
        marginLeft: '-5px',
        marginRight: '10px',
    },
    angleDownIcon: {
        width: '14px',
        marginRight: '5px'
    }
}

const optionClass = err => { return err ? 'option-list-item-error' : 'option-list-item' };

const pharmacyDictionary = genentechHubPharmacyDictionary;
const getEmptyDrug = drugName => {
    return {
        drugName: drugName,
        prescriptionType: null,
        prescriptionOption: null,
        numOfTablets: null,
        dosages: [], //[{NumberOfTablets: int, Dosage: decimal, DosageUnit: string}]
        dispenseDetails: [], //[{DispenseQuantity: decimal, DispenseUnitOptions: string}]
        frequencyOfAdministration: null,
        sig: null,
        daysOff: null,
        numOfTabletsPerCycle: null,
        numOfCyclesPerFill: null,
        refills: null,
        shipmentOption: null
    }
}
const isDefinedFieldType = type => {
    return type === genentechHubFieldTypes.Num ||
        type === genentechHubFieldTypes.Num5 ||
        type === genentechHubFieldTypes.Num5x3 ||
        type === genentechHubFieldTypes.String50 ||
        type === genentechHubFieldTypes.String150;
}

const GenetechHubPharmacyInput = props => {
    // eslint-disable-next-line no-unused-vars
    const { def, dataKey, data, onChange, state, warning } = props;
    const ctx = useContext(FormContext);

    const [errorList, setErrorList] = useState([]); //for highlighting error fields and keeping track of errors
    const [drugDetailList, setDrugDetailList] = useState([]);

    //Initialize with data from FormAnswer
    useEffect(() => {
        if (data && typeof data === 'string') {
            setDrugDetailList(JSON.parse(data));
        }
        else {
            const canonicalName = ctx.meta?.brandName
                ? (pharmacyDictionary.find(d => d.drugName.toLowerCase() === ctx.meta.brandName.toLowerCase())?.drugName ?? ctx.meta.brandName)
                : null;
            setDrugDetailList([getEmptyDrug(canonicalName)]);
        }
    
    }, [])

    //Managing details object as the "data" for
    const addDrugToDetails = (drugName, index) => {
        var drugDetail = getEmptyDrug(drugName);
        if (index >= 0) {
            var details = drugDetailList;
            details.splice(index, 0, drugDetail);
            setDrugDetailList([...details]);
        }
        else {
            setDrugDetailList([...drugDetailList, drugDetail]);
        }
        setDrugToAdd(null);
    }

    const removeDrugFromDetails = (index) => {
        var details = drugDetailList;
        details.splice(index, 1);
        setDrugDetailList([...details]);
        setDrugToAdd(null);
    }

    const updateDrugDetails = (index, propertyName, value) => {
        var details = drugDetailList;
        details[index][propertyName] = value;
        setDrugDetailList([...details]);
    }

    const resetFieldsAfterPrescription = (index, optionDictionary) => {
        var details = drugDetailList;
        details[index]['numOfTablets'] = null;
        details[index]['dosages'] = []; //[{NumberOfTablets: int, Dosage: decimal, DosageUnit: string}]
        details[index]['dispenseDetails'] = []; //[{DispenseQuantity: decimal, DispenseUnitOptions: string}]
        details[index]['frequencyOfAdministration'] = null;
        details[index]['sig'] = null;
        details[index]['daysOff'] = null;
        details[index]['numOfTabletsPerCycle'] = null;
        details[index]['numOfCyclesPerFill'] = null;
        details[index]['refills'] = null;

        //Specialty details setup for dosage and dispense since they are arrays of {Qty: int, Unit: string}
        if (optionDictionary.dosage === genentechHubFieldTypes.Num5x3) details[index]['dosages'] = [{ NumberOfTablets: null, Dosage: null, DosageUnit: optionDictionary.dosageUnit }, { NumberOfTablets: null, Dosage: null, DosageUnit: optionDictionary.dosageUnit }, { NumberOfTablets: null, Dosage: null, DosageUnit: optionDictionary.dosageUnit }];
        else if (optionDictionary.dosage) details[index]['dosages'] = [{ NumberOfTablets: null, Dosage: null, DosageUnit: optionDictionary.dosageUnit }];

        var dispenseArray = optionDictionary.dispenseUnitOptions?.map(opt => { return { DispenseQuantity: null, DispenseUnitOptions: opt } });
        if (dispenseArray) details[index]['dispenseDetails'] = dispenseArray;

        //Setup details for static/readonly fields
        for (const [key, value] of Object.entries(optionDictionary)) {
            if (!isDefinedFieldType(value) && (typeof value === 'string' || typeof value === 'number')) {
                switch (key) {  //keys are from GenentechHubPharmacyDictionary
                    case 'dosage':
                        details[index]['dosages'][0].Dosage = value;
                        break;
                    case 'dispenseQuantity':
                        details[index]['dispenseDetails'][0].DispenseQuantity = value;
                        break;
                    default:
                        details[index][key] = value;
                }

            }
        }

        setDrugDetailList([...details]);
    }

    //Perform updates when drugDetailList model changes (e.g. check for empty properties and display error)
    useEffect(() => {

        onChange(dataKey, JSON.stringify(drugDetailList));

        if (drugDetailList.length > 0) {
            var hasVenclexta = drugDetailList.some(drug => drug.drugName === drugList.Venclexta);
            venclextaSpecialRule(hasVenclexta, ctx);
        }
    
    }, [drugDetailList])

    //Managing errors for incomplete fields
    const pushErrorToList = key => {
        setErrorList([...errorList, key]);
    }

    const popErrorFromList = key => {
        setErrorList(errorList.filter(k => k !== key));
    }

    const removeErrorsForDrugIndex = drugIndex => {
        setErrorList(errorList.filter(key => !key.startsWith(drugIndex + '-')))
    };

    const removeInvalidDrugs = () => {
        const drugsToConsider = drugDetailList.slice(1);

        if (!drugsToConsider || drugsToConsider.length === 0) return;

        const drugsToKeep = drugsToConsider.filter(d => {
            const drugToConsider = pharmacyDictionary.filter(pd => pd.drugName === d.drugName)[0];
            return drugToConsider.shipmentOptions.includes('Replacement');
        });

        setDrugDetailList([drugDetailList[0], ...drugsToKeep]);
    }


    //Managing additional drug options
    const [drugToAdd, setDrugToAdd] = useState(null);
    const addOnOptions = ctx.meta?.brandName
        ? (pharmacyDictionary.find(d => d.drugName.toLowerCase() === ctx.meta.brandName.toLowerCase())?.addOnOptions ?? null)
        : null;

    const drugsThatCanBeAdded = () => {
        if (!addOnOptions) return [];
        let myAddOns = addOnOptions;

        //If a drug above has replacement, then only drugs with replacement in their ship options are valid
        if (myAddOns && drugDetailList.length > 0) {
            const first = drugDetailList[0];
            //drug must be in addOns, and either no ship option is selected, or the drug must have a matching ship option to the first drug
            const dcAddOns = pharmacyDictionary.filter(pd => myAddOns.includes(pd.drugName) && (pd.shipmentOptions.includes(first.shipmentOption) || !first.shipmentOption));
            myAddOns = dcAddOns.map(dc => dc.drugName);
        }

        if (myAddOns && drugDetailList?.length === 1) return myAddOns?.filter(opt => drugDetailList.findIndex(d => d.drugName === opt) === -1);
        if (drugDetailList?.length === 2) {
            //There are some drugs (e.g. Venclexta and Xeloda) that can have up to 2 prescriptions of different prescription types for the same drug
            if (drugDetailList[0].drugName === drugDetailList[1].drugName) return myAddOns?.filter(opt => drugDetailList.findIndex(d => d.drugName === opt) === -1);

            //Otherwise, there are the triple combo options
            var hasCotellic = drugDetailList?.findIndex(d => d.drugName === drugList.Cotellic) > -1;
            var hasTecentriq = drugDetailList?.findIndex(d => d.drugName === drugList.Tecentriq) > -1;
            var hasZelboraf = drugDetailList?.findIndex(d => d.drugName === drugList.Zelboraf) > -1;
            if (hasCotellic && hasTecentriq) return [drugList.Zelboraf];
            if (hasCotellic && hasZelboraf) return [drugList.Tecentriq];
            if (hasTecentriq && hasZelboraf) return [drugList.Cotellic];
        }
        return [];
    }
    const remainingOptions = drugsThatCanBeAdded();

    const canAddDuplicate = drugName => {
        const dictEntry = pharmacyDictionary.find(elem => elem.drugName === drugName);
        if (!dictEntry) return false;
        return drugDetailList.filter(elem => elem.drugName === drugName).length < 2 &&
            dictEntry.options[0]?.prescriptionType !== null;
    }
    const selectedPrescriptionTypes = drugDetailList.map(elem => elem.prescriptionType);

    return <>
        {drugDetailList?.length > 0 && drugDetailList.map((d, i) => {
            const first = drugDetailList[0];
            d.shipmentOption = first.shipmentOption;

            return <DrugDetails
                values={d}
                drugIndex={i}
                key={d.drugName + '-' + i}
                onPropertyChange={updateDrugDetails}
                addError={pushErrorToList}
                removeError={popErrorFromList}
                removeErrorsForDrugIndex={removeErrorsForDrugIndex}
                addDrugToDetails={addDrugToDetails}
                removeDrugFromDetails={removeDrugFromDetails}
                canAddDuplicate={canAddDuplicate(d.drugName)}
                selectedPrescriptionTypes={selectedPrescriptionTypes}
                resetFieldsAfterPrescription={resetFieldsAfterPrescription}
                firstShipOption={first.shipmentOption}
                removeInvalidDrugs={removeInvalidDrugs}
                data-input={'genentechhubpharmacyinput_de'}
            />
        })}


        {remainingOptions?.length > 0 &&
            <div style={{ ...styles.row, minWidth: '385px' }}>
                <Select
                    style={{ width: '200px' }}
                    value={drugToAdd}
                    options={remainingOptions.map(o => { return { label: drugListDisplayNames[o], value: o } })}
                    placeholder={'Additional Drug'}
                    onChange={(e, { value }) => setDrugToAdd(value)}
                    suffixIcon={<ImageContainer iconName='angle-down' styles={styles.angleDownIcon} className='fontawesomeTextGrey' />}
                    className={'selectinput_de'}
                />
                <Button
                    style={{ minWidth: '185px', minHeight: '38px' }}
                    icon={<ImageContainer iconName='plus' styles={{ ...styles.plusIcon, marginLeft: '-15px', marginRight: '20px' }} className='fontawesomeTextGrey' />}
                    disabled={!drugToAdd}
                    onClick={() => addDrugToDetails(drugToAdd)}
                    className='button_de'
                >
                    Add Prescription
                </Button>
            </div>
        }
    </>
}

const DrugDetails = ({ values, drugIndex, onPropertyChange, addError, removeError, removeErrorsForDrugIndex, addDrugToDetails, removeDrugFromDetails, removeInvalidDrugs, canAddDuplicate, selectedPrescriptionTypes, resetFieldsAfterPrescription, firstShipOption }) => {
    const [optionDictionary, setOptionDictionary] = useState({})
    const [selectedType, setSelectedType] = useState(null);
    const [selectedOption, setSelectedOption] = useState(null);
    const [selectedShipmentOption, setSelectedShipmentOption] = useState(null);
    const dictEntry = values.drugName
        ? pharmacyDictionary.find(d => d.drugName.toLowerCase() === values.drugName.toLowerCase())
        : null;
    const drugOptions = dictEntry?.options ?? [];
    const prescriptionTypeOptions = drugOptions.filter((o, i, a) => o.prescriptionType && a.findIndex(e => e.prescriptionType === o.prescriptionType) === i)
        .filter(o => selectedPrescriptionTypes.indexOf(o.prescriptionType) === -1 || o.prescriptionType === values.prescriptionType)
        .map(o => { return { label: o.prescriptionType, value: o.prescriptionType } });
    const prescriptionOptions = drugOptions.filter(o => o.prescriptionType === selectedType)
        .map(o => { return { label: o.prescriptionOption, value: o.prescriptionOption } });

    const shipmentOptions = !drugIndex
        ? (dictEntry?.shipmentOptions ?? [])
        : [firstShipOption];

    useEffect(() => {
        if (values && values.shipmentOption) {
            setSelectedShipmentOption(values.shipmentOption);
        }
        if (values && values.prescriptionOption) {
            const options = drugOptions.find(o => (o.prescriptionType === values.prescriptionType || (o.prescriptionType === null && values.prescriptionType === '')) && o.prescriptionOption === values.prescriptionOption);
            setOptionDictionary(options);
            setSelectedType(values.prescriptionType ? values.prescriptionType : null);
            setSelectedOption(values.prescriptionOption);
        }
    
    }, [])

    const onShipmentOptionChange = (value, drugIndex) => {
        setSelectedShipmentOption(value);
        onPropertyChange(drugIndex, 'shipmentOption', value);
        if (value === 'Replacement') removeErrorsForDrugIndex(drugIndex);
        if (value === 'Replacement' && drugIndex === 0) removeInvalidDrugs();
    }

    const onPrescriptionTypeChange = (value) => {
        setSelectedType(value);
        onPrescriptionOptionChange(null);
        onPropertyChange(drugIndex, 'prescriptionType', value);
    }

    const onPrescriptionOptionChange = (value) => {
        setSelectedOption(value);
        onPropertyChange(drugIndex, 'prescriptionOption', value);
        if (value === null) {
            setOptionDictionary({});
            return;
        }
        const options = drugOptions.find(o => o.prescriptionOption === value && o.prescriptionType === selectedType);
        setOptionDictionary(options);
        resetFieldsAfterPrescription(drugIndex, options);
    }

    const onDosageChange = (value, index, prop) => {
        var dosages = values.dosages;
        dosages[index][prop] = value;
        onPropertyChange(drugIndex, 'dosages', dosages);
    }

    const onDispenseQtyChange = (value, index, unit) => {
        var dispenseDetails = values.dispenseDetails;
        dispenseDetails[index] = { DispenseQuantity: value, DispenseUnitOptions: unit };
        onPropertyChange(drugIndex, 'dispenseDetails', dispenseDetails);
    }

    const disablePrescriptionOptions = prescriptionTypeOptions.length > 0 && !selectedType;

    return <>
        <div style={{ ...styles.row, minHeight: '36px', marginBottom: '0px' }}>
            <InfoCallOut>{`Prescription for ${drugListDisplayNames[values.drugName]}:`}</InfoCallOut>
            {canAddDuplicate && <Button
                style={{ minWidth: '40px', minHeight: '38px' }}
                icon={<ImageContainer iconName='plus' styles={styles.plusIcon} className='fontawesomeTextGrey' />}
                onClick={() => addDrugToDetails(values.drugName, drugIndex + 1)}
                className='button_de'
            />}
            {drugIndex > 0 && <Button
                style={{ minWidth: '185px', minHeight: '38px', marginLeft: '5px' }}
                icon={<ImageContainer iconName='xmark' styles={styles.closeIcon} className='fontawesomeTextGrey' />}
                onClick={() => removeDrugFromDetails(drugIndex)}
                className='button_de'>
                Remove Prescription
            </Button>}
        </div>
        <Group>
            <DropdownInput
                selectedOption={selectedShipmentOption}
                optionList={shipmentOptions.map(o => { return { label: o, value: o }; })}
                onChange={(v) => onShipmentOptionChange(v, drugIndex)}
                placeholder={'Shipment Option'}
            />
        </Group>
        {
            selectedShipmentOption === 'Upfront' && <>
                <Group>
                    {prescriptionTypeOptions.length > 0 && <DropdownInput selectedOption={selectedType} optionList={prescriptionTypeOptions} onChange={(v) => onPrescriptionTypeChange(v)} placeholder={`Prescription Type`} />}
                    <DropdownInput
                        selectedOption={selectedOption}
                        optionList={prescriptionOptions}
                        onChange={(v) => onPrescriptionOptionChange(v)}
                        placeholder={`Prescription Option`}
                        disabled={disablePrescriptionOptions} />
                </Group>
                {!checkIfObjectIsEmpty(optionDictionary) &&
                    <>
                        {(optionDictionary.numOfTablets || optionDictionary.dosage) &&
                            <Group>
                                <InputFetcher valueType={optionDictionary.numOfTablets} value={values.numOfTablets} placeholder='# of Tablets'
                                    onChange={(v) => { onPropertyChange(drugIndex, 'numOfTablets', v); onDosageChange(v, 0, 'NumberOfTablets'); }}
                                    addError={() => addError(`${drugIndex}-numOfTablets`)}
                                    removeError={() => removeError(`${drugIndex}-numOfTablets`)}
                                />
                                {values.dosages?.length > 0 &&
                                    <div style={{ ...styles.column, width: '100%' }} >
                                        {values.dosages.map((d, i) =>
                                            <div style={{ ...styles.row, width: '100%', gap: '10px' }} key={'dosage' + i}>
                                                <InputFetcher valueType={optionDictionary.dosage} value={values.dosages[i]?.Dosage} placeholder='Dosage'
                                                    onChange={(v) => onDosageChange(v, i, 'Dosage')}
                                                    addError={() => addError(`${drugIndex}-dosageQty-${i}`)}
                                                    removeError={() => removeError(`${drugIndex}-dosageQty-${i}`)}
                                                />
                                                <InputFetcher valueType={optionDictionary.dosageUnit} value={values.dosages[i]?.DosageUnit} placeholder='Dosage Unit'
                                                    onChange={(v) => onDosageChange(v, i, 'DosageUnit')}
                                                    addError={() => addError(`${drugIndex}-dosageUnit-${i}`)}
                                                    removeError={() => removeError(`${drugIndex}-dosageUnit-${i}`)}
                                                />
                                            </div>
                                        )}
                                    </div>
                                }
                            </Group>
                        }
                        {optionDictionary.sig &&
                            <Group>
                                <InputFetcher valueType={optionDictionary.sig} value={values.sig} placeholder='SIG'
                                    onChange={(v) => onPropertyChange(drugIndex, 'sig', v)}
                                    addError={() => addError(`${drugIndex}-sig`)}
                                    removeError={() => removeError(`${drugIndex}-sig`)}
                                />
                            </Group>
                        }
                        {(optionDictionary.frequencyOfAdministration || optionDictionary.refills) &&
                            <Group>
                                <InputFetcher valueType={optionDictionary.frequencyOfAdministration} value={values.frequencyOfAdministration} placeholder='Frequency of Administration'
                                    onChange={(v) => onPropertyChange(drugIndex, 'frequencyOfAdministration', v)}
                                    addError={() => addError(`${drugIndex}-frequencyOfAdministration`)}
                                    removeError={() => removeError(`${drugIndex}-frequencyOfAdministration`)}
                                />
                                <InputFetcher valueType={optionDictionary.refills} value={values.refills} placeholder='Refill(s)'
                                    onChange={(v) => onPropertyChange(drugIndex, 'refills', v)}
                                    addError={() => addError(`${drugIndex}-refills`)}
                                    removeError={() => removeError(`${drugIndex}-refills`)}
                                />
                            </Group>
                        }
                        {optionDictionary.dispenseUnitOptions && optionDictionary.dispenseUnitOptions.map((duo, i) =>
                            <Group key={duo}>
                                <InputFetcher valueType={optionDictionary.dispenseQuantity} value={values.dispenseDetails[i]?.DispenseQuantity} placeholder='Dispense'
                                    onChange={(v) => onDispenseQtyChange(v, i, duo)}
                                    addError={() => addError(`${drugIndex}-${duo}`)}
                                    removeError={() => removeError(`${drugIndex}-${duo}`)}
                                />
                                <InputFetcher valueType={duo} value={duo} onChange={() => { }} />
                                <InputFetcher valueType={optionDictionary.daysOff} value={values.daysOff} placeholder='Days off'
                                    onChange={(v) => onPropertyChange(drugIndex, 'daysOff', v)}
                                    addError={() => addError(`${drugIndex}-daysOff`)}
                                    removeError={() => removeError(`${drugIndex}-daysOff`)}
                                />
                            </Group>
                        )}
                        {(optionDictionary.numOfTabletsPerCycle || optionDictionary.numOfCyclesPerFill) &&
                            <Group>
                                <InputFetcher valueType={optionDictionary.numOfTabletsPerCycle} value={values.numOfTabletsPerCycle} placeholder='# tablets per cycle'
                                    onChange={(v) => onPropertyChange(drugIndex, 'numOfTabletsPerCycle', v)}
                                    addError={() => addError(`${drugIndex}-numOfTabletsPerCycle`)}
                                    removeError={() => removeError(`${drugIndex}-numOfTabletsPerCycle`)}
                                />
                                <InputFetcher valueType={optionDictionary.numOfCyclesPerFill} value={values.numOfCyclesPerFill} placeholder='# cycles per fill'
                                    onChange={(v) => onPropertyChange(drugIndex, 'numOfCyclesPerFill', v)}
                                    addError={() => addError(`${drugIndex}-numOfCyclesPerFill`)}
                                    removeError={() => removeError(`${drugIndex}-numOfCyclesPerFill`)}
                                />
                            </Group>
                        }
                    </>
                }
            </>
        }
        <Divider style={{ margin: '14px 0px', borderColor: 'lightgray' }} />
    </>
}

const InputFetcher = ({ valueType, value, placeholder, onChange, addError, removeError }) => {
    if (valueType === null) {
        return null;
    }
    else if (valueType === genentechHubFieldTypes.Num) {
        //number no max length
        return <NumberInput value={value} placeholder={placeholder} onChange={onChange} addError={addError} removeError={removeError} />
    }
    else if (valueType === genentechHubFieldTypes.Num5) {
        //number up to 5 characters long
        return <NumberInput value={value} placeholder={placeholder} maxLength={5} onChange={onChange} addError={addError} removeError={removeError} />
    }
    else if (valueType === genentechHubFieldTypes.Num5x3) { //the triplicate is taken care of elsewhere
        //number up to 5 characters long
        return <NumberInput value={value} placeholder={placeholder} maxLength={5} onChange={onChange} addError={addError} removeError={removeError} />
    }
    else if (valueType === genentechHubFieldTypes.String50) {
        //string up to 50 characters
        return <StringInput value={value} placeholder={placeholder} maxLength={50} onChange={onChange} addError={addError} removeError={removeError} />
    }
    else if (valueType === genentechHubFieldTypes.String150) {
        //string up to 150 characters
        return <StringInput value={value} placeholder={placeholder} maxLength={150} onChange={onChange} addError={addError} removeError={removeError} />
    }
    else if (typeof valueType === 'string' || typeof valueType === 'number') {
        //static readonly string
        return <StringInput value={valueType} placeholder={placeholder} onChange={onChange} readOnly={true} />
    }
    else if (Array.isArray(valueType)) {
        return <DropdownInput
            selectedOption={value}
            optionList={valueType.map(o => { return { label: o, value: o } })}
            placeholder={placeholder}
            onChange={onChange}
            addError={addError}
            removeError={removeError}
        />
    }
}

const NumberInput = props => {
    const { value, placeholder, maxLength, onChange, addError, removeError } = props;
    const [val, setVal] = useState(value === null ? '' : value);
    const [error, setError] = useState(value ? false : true);
    const err = error ? "error" : "";

    useEffect(() => {
        setVal(value === null ? '' : value); //for when value gets changed outside this control
        setError(value ? false : true);
    }, [value])

    const checkNumeric = (v) => {
        if (isNaN(v) && v !== '') return;
        if (maxLength && v.length > maxLength) return;
        if (v < 0) return;
        if (!v && !error) {
            addError();
            setError(true);
        }
        else if (v && error) {
            removeError();
            setError(false);
        }
        onChange(v);
        setVal(v);
    }

    return <Column>
        <InputLabel text={placeholder} />
        <Input
            value={val}
            placeholder={placeholder}
            onChange={(e) => checkNumeric(e.target.value)}
            status={err}
            className={'basicinput_de'}
        />
    </Column>
}

const StringInput = props => {
    const { value, placeholder, maxLength, onChange, readOnly, addError, removeError } = props;
    const disabled = readOnly || false;
    const [val, setVal] = useState(value === null ? '' : value);
    const [error, setError] = useState(value ? false : true);
    const err = error ? "error" : "";

    useEffect(() => {
        setVal(value === null ? '' : value); //for when value gets changed outside this control
        if (readOnly) onChange(value); //to actually set the drugDetailList value to be the dictionary's static value
        setError(value ? false : true);
    
    }, [value])

    const checkString = (v) => {
        if (disabled) return;
        if (maxLength && v.length > maxLength) return;
        if (!v && !error) {
            addError();
            setError(true);
        }
        else if (v && error) {
            removeError();
            setError(false);
        }
        onChange(v);
        setVal(v);
    }

    return <Column error={error}>
        <InputLabel text={placeholder} />
        <Input
            value={val}
            placeholder={placeholder}
            onChange={(e) => checkString(e.target.value)}
            status={err}
            className={'basicinput_de'}
        />
    </Column>
}

const DropdownInput = props => {
    const { selectedOption, optionList, onChange, placeholder, addError, removeError, disabled } = props;
    const isDisabled = disabled || false;
    const [val, setVal] = useState(selectedOption);
    const [error, setError] = useState((selectedOption || !isNaN(parseInt(selectedOption))) ? false : true);
    const err = error ? "error" : "";
    optionList.forEach(o => o.className = optionClass(err));

    useEffect(() => {
        setVal(selectedOption); //for when selectedOption gets changed outside this control
        setError((selectedOption || !isNaN(parseInt(selectedOption))) ? false : true);
    }, [selectedOption])

    const onSelect = v => {
        if ((!v && isNaN(parseInt(v))) && !error) {
            if (addError) addError();
            setError(true);
        }
        else if ((v || !isNaN(parseInt(v))) && error) {
            if (removeError) removeError();
            setError(false);
        }
        onChange(v);
        setVal(v);
    }

    return <Column>
        <InputLabel text={placeholder} />
        <Select
            value={val}
            options={optionList}
            disabled={isDisabled}
            placeholder={placeholder}
            onChange={(e, { value }) => onSelect(value)}
            status={err}
            suffixIcon={<ImageContainer iconName='angle-down' styles={styles.angleDownIcon} className='fontawesomeTextGrey' />}
            className={'selectinput_de'}
        />
    </Column>
}

const InputLabel = props => {
    const { text } = props;
    const style = {
        height: '18px',
        marginTop: '-2px'
    };
    return <div style={style}>
        <LabelText label={text} />
    </div>
}

const Column = props => {
    const { children } = props;
    return <div style={{ ...styles.column, flexGrow: 1, width: '100%' }}>
        {children}
    </div>
}

const Group = props => {
    const { children } = props;
    return <div style={{ ...styles.row, gap: '10px' }}>
        {children}
    </div>
}

export default GenetechHubPharmacyInput;
